/**
 * A trigger: when a rule holds for one entry. An AND of ORs over the entry's
 * fields, two levels deep and never deeper.
 *
 * The grammar was born with Links (Section 5.7) and outlived them: Gmail rules
 * (connectors/rules.ts, hits.ts, entries-where.ts) and task rules
 * (task-rules.ts) read and judge their conditions with it. Phase 9 of
 * docs/plans/ledger.md moved it here when the Link engine was removed.
 *
 * It lives in shared on purpose: the server and any device judge a condition
 * with the same code.
 */

import {
  DATE_CONDITION_OPS,
  dateConditionHolds,
  dateConditionValueProblem,
  isDateConditionOp,
  type DateConditionOp,
} from '../date-conditions';
import type { CoversAll } from '../covers-all';

/**
 * One comparison inside a trigger.
 *
 * `field` names a field on the SOURCE record. `op` is closed, so a trigger is
 * never an expression anybody has to parse. `values` holds the alternatives
 * for that one field, and any one of them is enough: that is the OR a person
 * means when they say "cash or online".
 */
export type TriggerCondition = {
  fieldId: string;
  op: TriggerConditionOp;
  /** Empty on `filled` and `empty`. One entry only on a numeric op. */
  values: readonly (string | number)[];
  /**
   * A reference field on the SOURCE, when the question is asked of the row it
   * points at: "only when the customer's Payment terms are Credit".
   *
   * With `via` set, `fieldId` names a field on that other record instead of on
   * the source. Depth stays one hop, the same rule a formula keeps, and the
   * evaluator needs a reader to answer at all — without one the condition
   * holds nothing rather than guessing.
   */
  via?: string;
};

export type TriggerConditionOp =
  | 'is'
  | 'is_not'
  | 'filled'
  | 'empty'
  | 'at_least'
  | 'at_most'
  | 'exactly'
  | 'contains'
  | 'not_contains'
  // The date conditions of F21 (docs/plans/field-relations.md), on any stored
  // date, worked out with the caller's day: `date-conditions.ts`.
  | DateConditionOp;

/** Every condition op, in the order of the type. */
export const TRIGGER_CONDITION_OPS = [
  'is',
  'is_not',
  'filled',
  'empty',
  'at_least',
  'at_most',
  'exactly',
  'contains',
  'not_contains',
  ...DATE_CONDITION_OPS,
] as const satisfies readonly TriggerConditionOp[];
true satisfies CoversAll<TriggerConditionOp, typeof TRIGGER_CONDITION_OPS>;

/** The ops that need no value at all. */
const VALUELESS: ReadonlySet<TriggerConditionOp> = new Set(['filled', 'empty', 'is_today', 'before_today', 'after_today']);

/** The ops that compare numbers, and so hold exactly one value. A count of days is one. */
const NUMERIC: ReadonlySet<TriggerConditionOp> = new Set(['at_least', 'at_most', 'exactly', 'in_next_days', 'in_last_days']);

/** The ops that ask about a date, read with the caller's day (F21). */
export const opIsDate = (op: TriggerConditionOp): op is DateConditionOp => isDateConditionOp(op);

export const opNeedsValue = (op: TriggerConditionOp): boolean => !VALUELESS.has(op);
export const opIsNumeric = (op: TriggerConditionOp): boolean => NUMERIC.has(op);

/**
 * One group of conditions. Any one of them is enough.
 *
 * A group with a single condition is the common case, and a condition holding
 * several values is the second most common. A group with several conditions on
 * DIFFERENT fields is the cross-field OR.
 */
export type TriggerConditionGroup = { any: readonly TriggerCondition[] };

/**
 * When a rule holds: an AND of ORs, two levels deep and never deeper.
 *
 * `all` is joined by AND. Each group's `any` is joined by OR. Every condition
 * a business can state fits this shape, because any boolean expression can be
 * written as an AND of ORs. So there are no brackets, no precedence, and the
 * evaluator is two nested loops rather than a parser.
 *
 * An empty `all` means always. That is the `{}` the column defaults to.
 */
export type Trigger = { all: readonly TriggerConditionGroup[] };

/* ------------------------------------------------------------- the evaluator */

/** A field-id keyed entry, the shape both sides already pass around. */
export type TriggerValues = Readonly<Record<string, unknown>>;

/** True when the value is present in the sense `filled` means. */
const isFilled = (value: unknown): boolean =>
  value !== null && value !== undefined && value !== '' && !(Array.isArray(value) && value.length === 0);

const asText = (value: unknown): string => (value === null || value === undefined ? '' : String(value));

/**
 * The value a condition is asking about: on this entry, or on the row it
 * points at. Undefined when the hop cannot be made, so `filled` says no and
 * `empty` says yes, which is the honest reading of a reference nobody set.
 */
const askedValue = (
  condition: TriggerCondition,
  values: TriggerValues,
  readReference: ReferenceReader | null,
): unknown => {
  if (condition.via === undefined) return values[condition.fieldId];
  const pointed = values[condition.via];
  if (typeof pointed !== 'string' || pointed === '' || readReference === null) return undefined;
  return readReference(pointed, condition.fieldId);
};

/**
 * One condition against one entry. Unknown ops refuse rather than pass.
 *
 * `readReference` is only ever used by a condition with a `via`, and passing
 * null refuses every one of them — which is what a preview with nothing loaded
 * should do, and what every caller written before hop conditions existed does
 * by leaving the argument off.
 */
export const conditionHolds = (
  condition: TriggerCondition,
  values: TriggerValues,
  readReference: ReferenceReader | null = null,
  /** The caller's day, `YYYY-MM-DD`: the workspace's on the server. Null: no date condition holds. */
  today: string | null = null,
): boolean => {
  const held = askedValue(condition, values, readReference);

  if (opIsDate(condition.op)) return dateConditionHolds(condition.op, condition.values[0], held, today);

  if (condition.op === 'filled') return isFilled(held);
  if (condition.op === 'empty') return !isFilled(held);
  if (condition.values.length === 0) return false;

  if (condition.op === 'is' || condition.op === 'is_not') {
    // A multi-value field holds a list, so membership is the test either way.
    const carried = Array.isArray(held) ? held.map(asText) : [asText(held)];
    const wanted = condition.values.map(asText);
    const found = carried.some((one) => wanted.includes(one));
    return condition.op === 'is' ? found : !found;
  }

  if (opIsNumeric(condition.op)) {
    const left = Number(held);
    const right = Number(condition.values[0]);
    if (!Number.isFinite(left) || !Number.isFinite(right)) return false;
    if (condition.op === 'at_least') return left >= right;
    if (condition.op === 'at_most') return left <= right;
    return left === right;
  }

  const text = asText(held).toLowerCase();
  const found = condition.values.some((one) => text.includes(asText(one).toLowerCase()));
  return condition.op === 'contains' ? found : !found;
};

/**
 * Whether a trigger holds for this entry.
 *
 * Two nested loops, in this order: every group must hold, and a group holds
 * when any one of its conditions holds. An empty `all` means always, which is
 * what the column's `{}` default says.
 */
export const triggerHolds = (
  trigger: Trigger,
  values: TriggerValues,
  readReference: ReferenceReader | null = null,
  /** The caller's day, for a date condition (F21). Null: no date condition holds. */
  today: string | null = null,
): boolean => {
  if (trigger.all.length === 0) return true;
  return trigger.all.every(
    (group) =>
      group.any.length > 0 &&
      group.any.some((condition) => conditionHolds(condition, values, readReference, today)),
  );
};

/** True when a trigger holds a date condition, so its caller must hand it a day. */
export const triggerReadsToday = (trigger: Trigger): boolean =>
  trigger.all.some((group) => group.any.some((condition) => opIsDate(condition.op)));

/** How a hop reads the row an entry's reference points at: (row id, field id) to its value. */
export type ReferenceReader = (entryId: string, fieldId: string) => unknown;

/* --------------------------------------------------------------- validation */

/** The trigger shape, checked without trusting the caller. Depth is fixed at two. */
export const readTrigger = (raw: unknown): Trigger | null => {
  if (raw === null || raw === undefined) return { all: [] };
  if (typeof raw !== 'object' || Array.isArray(raw)) return null;
  const held = (raw as { all?: unknown }).all;
  // `{}` is the column default and means always.
  if (held === undefined) return { all: [] };
  if (!Array.isArray(held)) return null;

  const groups: TriggerConditionGroup[] = [];
  for (const rawGroup of held) {
    if (typeof rawGroup !== 'object' || rawGroup === null) return null;
    const any = (rawGroup as { any?: unknown }).any;
    if (!Array.isArray(any) || any.length === 0) return null;

    const conditions: TriggerCondition[] = [];
    for (const rawCondition of any) {
      const read = readCondition(rawCondition);
      if (read === null) return null;
      conditions.push(read);
    }
    groups.push({ any: conditions });
  }
  return { all: groups };
};

const OPS: ReadonlySet<string> = new Set([
  'is',
  'is_not',
  'filled',
  'empty',
  'at_least',
  'at_most',
  'exactly',
  'contains',
  'not_contains',
  ...DATE_CONDITION_OPS,
]);

const readCondition = (raw: unknown): TriggerCondition | null => {
  if (typeof raw !== 'object' || raw === null) return null;
  const held = raw as { fieldId?: unknown; op?: unknown; values?: unknown; via?: unknown };
  if (typeof held.fieldId !== 'string' || held.fieldId === '') return null;
  if (typeof held.op !== 'string' || !OPS.has(held.op)) return null;
  const op = held.op as TriggerConditionOp;

  // Absent is the ordinary condition. Present and empty is a half-built hop,
  // which would silently read the source's own field instead.
  if (held.via !== undefined && (typeof held.via !== 'string' || held.via === '')) return null;
  const hop = held.via === undefined ? {} : { via: held.via as string };

  const rawValues = held.values ?? [];
  if (!Array.isArray(rawValues)) return null;
  const values: (string | number)[] = [];
  for (const one of rawValues) {
    if (typeof one !== 'string' && typeof one !== 'number') return null;
    values.push(one);
  }
  if (!opNeedsValue(op)) return { fieldId: held.fieldId, op, values: [], ...hop };
  if (values.length === 0) return null;
  // A range is two conditions, never one, so a numeric op holds one figure.
  if (opIsNumeric(op) && values.length !== 1) return null;
  // A date condition holds one count of days or one date, read as it is judged.
  if (opIsDate(op) && (values.length !== 1 || dateConditionValueProblem(op, values[0]) !== null)) return null;
  return { fieldId: held.fieldId, op, values, ...hop };
};

/** Every field on the entry a trigger reads. A hop condition reads its reference, not the far field. */
export const triggerFieldsRead = (trigger: Trigger): readonly string[] => {
  const out = new Set<string>();
  for (const group of trigger.all) for (const condition of group.any) out.add(condition.via ?? condition.fieldId);
  return [...out];
};

/**
 * Every pair a trigger reads through a reference: the reference field on the
 * entry, and the field it reads on the row it points at. The server loads
 * these up front, so `triggerHolds` stays synchronous. De-duplicated.
 */
export const triggerHopsRead = (trigger: Trigger): readonly { via: string; fieldId: string }[] => {
  const seen = new Set<string>();
  const out: { via: string; fieldId: string }[] = [];
  for (const group of trigger.all) {
    for (const condition of group.any) {
      if (condition.via === undefined) continue;
      const key = `${condition.via} ${condition.fieldId}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ via: condition.via, fieldId: condition.fieldId });
    }
  }
  return out;
};
